import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from "react";

import { confirmLeaveNavigation } from "../utils/confirmNavigation";

interface GuardEntry {
  id: string;
  when: boolean;
  message?: string;
}

interface UnsavedChangesContextValue {
  registerGuard: (entry: GuardEntry) => void;
  unregisterGuard: (id: string) => void;
  hasUnsavedChanges: boolean;
  confirmLeaveAll: () => Promise<boolean>;
}

const UnsavedChangesContext = createContext<UnsavedChangesContextValue>({
  registerGuard: () => {},
  unregisterGuard: () => {},
  hasUnsavedChanges: false,
  confirmLeaveAll: async () => true,
});

export const UnsavedChangesProvider = ({ children }: { children: ReactNode }) => {
  const guardsRef = useRef<Map<string, GuardEntry>>(new Map());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const syncDirtyState = useCallback(() => {
    const dirty = Array.from(guardsRef.current.values()).some((entry) => entry.when);
    setHasUnsavedChanges(dirty);
  }, []);

  const registerGuard = useCallback(
    (entry: GuardEntry) => {
      guardsRef.current.set(entry.id, entry);
      syncDirtyState();
    },
    [syncDirtyState]
  );

  const unregisterGuard = useCallback(
    (id: string) => {
      guardsRef.current.delete(id);
      syncDirtyState();
    },
    [syncDirtyState]
  );

  const confirmLeaveAll = useCallback(async () => {
    const activeGuards = Array.from(guardsRef.current.values()).filter(
      (entry) => entry.when
    );

    if (activeGuards.length === 0) {
      return true;
    }

    const message =
      activeGuards.find((entry) => entry.message)?.message ??
      undefined;

    return confirmLeaveNavigation(message);
  }, []);

  const value = useMemo(
    () => ({
      registerGuard,
      unregisterGuard,
      hasUnsavedChanges,
      confirmLeaveAll,
    }),
    [registerGuard, unregisterGuard, hasUnsavedChanges, confirmLeaveAll]
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
    </UnsavedChangesContext.Provider>
  );
};

export const useUnsavedChangesContext = () => useContext(UnsavedChangesContext);
